import React from 'react';
import { Loader2, AlertTriangle, X, DollarSign, Sparkles } from 'lucide-react';

// Shape returned at GET /regen/requests/:id → data.directive.dry_run_preview once a dry-run
// job lands on status='dry_run'. Varies by lane — every field is optional; render defensively.
export interface DryRunPreview {
  lane?: 'script' | 'hook' | 'caption' | string;
  original?: string;
  rewritten?: string;
  diagnosis?: {
    summary?: string;
    directive_hint?: string;
    lagging_metric?: string;
    recommended_type?: string;
    [k: string]: any;
  };
  metrics?: { roi_pct?: number; roas?: number; spend?: number; [k: string]: any };
  projected_usd?: number;
  winner_reference_used?: { filename?: string; roas?: number | string } | null;
  clips?: Array<{ index?: number; seconds?: number; prompt?: string; scene?: string; spoken_line?: string; [k: string]: any }>;
  [k: string]: any;
}

interface GenerationPreviewProps {
  /** The dry_run_preview payload. null/undefined while still loading. */
  preview: DryRunPreview | null | undefined;
  /** Still polling for the preview to land. */
  loading?: boolean;
  /** The real ("Generate for real") request is in flight. */
  busy?: boolean;
  /** Surface a preview-fetch or real-submit error inline instead of a silent close. */
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
}

/**
 * PREVIEW-FIRST gate for any generation flow: shows a dry-run's result (rewritten copy,
 * clip plan, diagnosis, projected cost) before anything spends, then lets the caller either
 * fire the real request or back out. Reusable — pass whatever dry_run_preview shape the lane
 * returned; unknown/missing fields are simply not rendered.
 */
export function GenerationPreview({ preview, loading, busy, error, onConfirm, onCancel, confirmLabel }: GenerationPreviewProps) {
  const hasRewrite = !!(preview && (preview.original || preview.rewritten));
  const hasClips = !!(preview && Array.isArray(preview.clips) && preview.clips.length > 0);
  const hasKnownShape = hasRewrite || hasClips;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Preview — nothing has been spent yet</h2>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-muted transition-colors" disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500 py-10">
              <Loader2 className="w-6 h-6 animate-spin" />
              Building the preview…
            </div>
          )}

          {!loading && preview && (
            <>
              {preview.diagnosis?.summary && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    Why this is changing{preview.diagnosis.lagging_metric ? ` — ${preview.diagnosis.lagging_metric}` : ''}
                  </div>
                  <p className="text-xs text-amber-900 dark:text-amber-200 mt-1">{preview.diagnosis.summary}</p>
                  {preview.diagnosis.directive_hint && (
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">→ {preview.diagnosis.directive_hint}</p>
                  )}
                </div>
              )}

              {hasRewrite && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Original</div>
                    <div className="rounded-lg border border-border p-3 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                      {preview.original || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Rewritten</div>
                    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-xs text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                      {preview.rewritten || '—'}
                    </div>
                  </div>
                </div>
              )}

              {preview.winner_reference_used && (
                <div className="text-xs text-gray-500">
                  Winner referenced (angle only, not copied): <span className="text-gray-700 dark:text-gray-300">
                    {preview.winner_reference_used.filename || 'a proven winner'}
                    {preview.winner_reference_used.roas != null ? ` (ROI ${Number.isFinite(Number(preview.winner_reference_used.roas)) ? `${Number(preview.winner_reference_used.roas) >= 1 ? '+' : ''}${((Number(preview.winner_reference_used.roas) - 1) * 100).toFixed(0)}%` : preview.winner_reference_used.roas})` : ''}
                  </span>
                </div>
              )}

              {hasClips && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Clip plan ({preview.clips!.length})</div>
                  <div className="space-y-2">
                    {preview.clips!.map((c, i) => (
                      <div key={i} className="rounded-lg border border-border p-2.5">
                        <div className="flex items-center justify-between text-[11px] text-gray-500">
                          <span>Clip {c.index != null ? c.index + 1 : i + 1}</span>
                          {c.seconds != null && <span>{c.seconds}s</span>}
                        </div>
                        {c.scene && <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5"><b>Scene:</b> {c.scene}</div>}
                        {c.spoken_line && <div className="text-xs text-gray-900 dark:text-white mt-0.5"><b>Says:</b> “{c.spoken_line}”</div>}
                        {!c.scene && !c.spoken_line && c.prompt && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 whitespace-pre-wrap break-words">{c.prompt}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasKnownShape && (
                <div className="rounded-lg border border-border p-3 text-xs text-gray-500">
                  <pre className="whitespace-pre-wrap break-words">{JSON.stringify(preview, null, 2)}</pre>
                </div>
              )}

              {(preview.projected_usd != null || preview.metrics) && (
                <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border text-xs">
                  {preview.projected_usd != null && (
                    <span className="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" /> ~${Number(preview.projected_usd).toFixed(2)} projected
                    </span>
                  )}
                  {preview.metrics?.roi_pct != null && <span className="text-gray-500">current ROI {preview.metrics.roi_pct}%</span>}
                  {preview.metrics?.spend != null && <span className="text-gray-500">spend ${preview.metrics.spend}</span>}
                </div>
              )}
            </>
          )}

          {!loading && !preview && !error && (
            <p className="text-xs text-gray-500">No preview data came back.</p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2 shrink-0">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || busy}
            className="px-4 py-2 text-sm font-medium rounded bg-primary text-primary-foreground hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : (confirmLabel || 'Generate for real →')}
          </button>
        </div>
      </div>
    </div>
  );
}
